import React, { useEffect, useRef, useState } from 'react'
import { Vehicle } from '../types'
import { apiClient, API_URLS, getCurrentConfig } from '../config/api'

interface FleetMapProps {
  vehicles: Vehicle[]
  selectedVehicle: Vehicle | null
  onVehicleSelect: (vehicle: Vehicle) => void
  timeRange?: number
}

const FleetMap: React.FC<FleetMapProps> = ({ 
  vehicles, 
  selectedVehicle, 
  onVehicleSelect,
  timeRange = 8760
}) => {
  const [mapReady, setMapReady] = useState(false)
  const [mapId] = useState(() => `map-${Date.now()}`) // Unique ID for this map
  const trajectoryRef = useRef<google.maps.Polyline | null>(null)

  useEffect(() => {
    // Wait for DOM to be ready, then initialize
    const timer = setTimeout(() => {
      initializeMap()
    }, 500) // Give DOM time to render

    return () => clearTimeout(timer)
  }, [])

  const initializeMap = () => {
    const mapElement = document.getElementById(mapId)
    
    if (!mapElement) {
      console.error('❌ Map element not found:', mapId)
      return
    }

    if (!window.google || !window.google.maps) {
      console.log('🗺️ Loading Google Maps API...')
      loadGoogleMapsAPI(() => {
        console.log('✅ Google Maps loaded, creating map...')
        createMap(mapElement)
      })
      return
    }

    createMap(mapElement)
  }

  const loadGoogleMapsAPI = (callback: () => void) => {
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      // Script already exists, wait for it
      const checkInterval = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(checkInterval)
          callback()
        }
      }, 100)
      return
    }

    const script = document.createElement('script')
        script.src = `https://maps.googleapis.com/maps/api/js?key=${getCurrentConfig().GOOGLE_MAPS_API_KEY}`
    script.onload = callback
    script.onerror = () => console.error('❌ Failed to load Google Maps')
    document.head.appendChild(script)
  }

  const createMap = (container: HTMLElement) => {
    try {
      // Get first vehicle position
      const firstVehicle = vehicles.find(v => 
        v.location.latitude && v.location.longitude && 
        v.location.latitude !== 0 && v.location.longitude !== 0
      )

      const center = firstVehicle 
        ? { lat: firstVehicle.location.latitude, lng: firstVehicle.location.longitude }
        : { lat: 12.5896, lng: -8.0207 }

      console.log('🗺️ Creating map at:', center)

      const map = new google.maps.Map(container, {
        zoom: 12,
        center,
        mapTypeId: google.maps.MapTypeId.ROADMAP
      })

      // Add vehicle markers
      vehicles.forEach(vehicle => {
        if (vehicle.location.latitude && vehicle.location.longitude &&
            !(vehicle.location.latitude === 0 && vehicle.location.longitude === 0)) {
          
          const marker = new google.maps.Marker({
            position: { lat: vehicle.location.latitude, lng: vehicle.location.longitude },
            map,
            title: vehicle.name,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              fillColor: vehicle.status === 'moving' ? '#10b981' : '#6b7280',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2,
              scale: 12
            }
          })

          marker.addListener('click', () => {
            console.log(`🎯 Vehicle clicked: ${vehicle.name}`)
            onVehicleSelect(vehicle)
            loadTrajectory(vehicle.id, map)
          })
        }
      })

      setMapReady(true)
      console.log('✅ Map created with', vehicles.length, 'vehicles')

    } catch (error) {
      console.error('❌ Map creation error:', error)
    }
  }

  const loadTrajectory = async (vehicleId: string, map: google.maps.Map) => {
    try {
      console.log(`🛣️ Loading trajectory for: ${vehicleId}`)
      const response = await apiClient.get(`/trajectory/${vehicleId}`, { hours: timeRange })
      console.log('📍 Trajectory API response:', response.data)
      
      if (response.data?.trajectory?.length > 0) {
        const trajectory = response.data.trajectory
        console.log(`✅ Trajectory loaded: ${trajectory.length} points`)
        console.log('🔍 First point:', trajectory[0])
        console.log('🔍 Last point:', trajectory[trajectory.length - 1])

        // Validate and filter trajectory points
        const validPoints = trajectory.filter((p: any) => 
          p.latitude && p.longitude && 
          !isNaN(p.latitude) && !isNaN(p.longitude) &&
          p.latitude !== 0 && p.longitude !== 0
        )
        
        console.log(`📍 Valid points: ${validPoints.length}/${trajectory.length}`)
        
        if (validPoints.length === 0) {
          console.error('❌ No valid trajectory points')
          alert('No valid GPS coordinates found')
          return
        }

        // Apply trajectory smoothing (remove GPS outliers)
        const smoothedPoints = smoothTrajectory(validPoints)
        console.log(`🎯 Smoothed points: ${smoothedPoints.length} (from ${validPoints.length})`)

        // Optimize for performance (limit to 500 points)
        const maxPoints = 500
        let finalPoints = smoothedPoints
        
        if (smoothedPoints.length > maxPoints) {
          const step = Math.floor(smoothedPoints.length / maxPoints)
          finalPoints = smoothedPoints.filter((_: any, index: number) => index % step === 0)
          console.log(`🎯 Optimized to ${finalPoints.length} points for performance`)
        }

        // Clear existing trajectory
        if (trajectoryRef.current) {
          trajectoryRef.current.setMap(null)
        }

        // Create trajectory path
        const path = finalPoints.map((p: any) => ({ lat: p.latitude, lng: p.longitude }))

        // Create polyline with enhanced styling
        trajectoryRef.current = new google.maps.Polyline({
          path,
          geodesic: true,
          strokeColor: '#3b82f6',
          strokeOpacity: 0.8,
          strokeWeight: 4,
          map
        })

        // Add start and end markers
        if (finalPoints.length > 0) {
          const startPoint = finalPoints[0]
          const endPoint = finalPoints[finalPoints.length - 1]

          // Start marker (green)
          new google.maps.Marker({
            position: { lat: startPoint.latitude, lng: startPoint.longitude },
            map,
            title: `Start: ${new Date(startPoint.timestamp).toLocaleString()}`,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              fillColor: '#22c55e',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2,
              scale: 8
            }
          })

          // End marker (red)
          new google.maps.Marker({
            position: { lat: endPoint.latitude, lng: endPoint.longitude },
            map,
            title: `End: ${new Date(endPoint.timestamp).toLocaleString()}`,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              fillColor: '#ef4444',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2,
              scale: 8
            }
          })
        }

        // Fit bounds to trajectory
        const bounds = new google.maps.LatLngBounds()
        path.forEach(point => bounds.extend(point))
        map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 })

        console.log('✅ Trajectory displayed with start/end markers')

      } else {
        console.log('❌ No trajectory data available')
        alert(`No trajectory data for vehicle: ${vehicleId}`)
      }
    } catch (error) {
      console.error('❌ Trajectory error:', error)
      alert('Error loading trajectory')
    }
          }

          return (
    <div className="h-full relative">
      <div id={mapId} className="h-full w-full" />
      
      {!mapReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <div className="text-gray-600">Loading Google Maps...</div>
          </div>
        </div>
      )}
      
      <div className="absolute bottom-4 left-4 bg-white rounded shadow p-2 text-sm">
        📍 {vehicles.length} vehicles
      </div>
    </div>
  )
}

// Trajectory smoothing function (from original FleetApp)
function smoothTrajectory(points: any[]): any[] {
  if (points.length < 3) return points

  const smoothed: any[] = []
  
  // Add first point
  smoothed.push(points[0])

  // Apply smoothing with outlier detection
  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1]
    const curr = points[i]
    const next = points[i + 1]

    // Calculate distance to detect GPS outliers
    const distToPrev = calculateDistance(prev.latitude, prev.longitude, curr.latitude, curr.longitude)
    const distToNext = calculateDistance(curr.latitude, curr.longitude, next.latitude, next.longitude)

    // Skip GPS outliers (sudden jumps > 500m)
    if (distToPrev > 0.5 || distToNext > 0.5) {
      console.log(`🚫 Skipping GPS outlier: distance ${distToPrev.toFixed(2)}km / ${distToNext.toFixed(2)}km`)
      continue
    }

    // Include all valid points (removed speed filter as many GPS points have speed=0)
    smoothed.push(curr)
  }

  // Add last point
  smoothed.push(points[points.length - 1])

  return smoothed
}

// Calculate distance between two GPS points (in km)
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371 // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLon = (lon2 - lon1) * Math.PI / 180
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

export default FleetMap
